import { Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuthStore } from '@features/auth'
import type { UserRole } from '@features/auth'
import { hasAnyCapability, isRoleAllowed } from '@features/auth/utils/access'
import type { Capability } from '@features/auth/utils/access'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: UserRole[]
  requiredCapabilities?: Capability[]
}

function ProtectedRoute({ children, allowedRoles, requiredCapabilities }: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading, user } = useAuthStore()

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const isPlatformRoleWithoutOrganization = user?.role === 'super_admin'
  if (user && !user.organization_id && !isPlatformRoleWithoutOrganization) {
    return <Navigate to="/register" replace />
  }

  if (user && allowedRoles && !isRoleAllowed(user.role, allowedRoles)) {
    return <Navigate to="/" replace />
  }

  if (user && requiredCapabilities && !hasAnyCapability(user.role, requiredCapabilities)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export default ProtectedRoute
